import {Injectable} from '@angular/core';
import {HttpInterceptorService} from '../../core/services/http-interceptor.service';
import {UserActionCreatorService} from './user-action-creator.service';
import {Observable} from 'rxjs/Observable';
import {Ipractitioner} from '../interfaces/practitioner.interface';
import {usersTypes} from '../constants/usersTypes.enum';
import {catchError, map, mergeMap, tap} from 'rxjs/operators';
import {Patient} from '../classes/patient.class';
import {GenericApiRequest, ISpecificMapItem} from '../../core/classes/generic-api-request.class';
import {DynamicFormItem} from '@ispm/dynamic-form/classes/dynamic-form-item.class';
import {ITEMS_TYPES} from '@ispm/dynamic-form/constants/ItemsTypes';
import {ConditionalOperator} from '@ispm/shared/constants/conditional-operator.enum';
import {ModalLoaderNgjsService} from '@ispm/layout/services/modal-loader-ngjs.service';
import {ConfirmModalLoaderPayload} from '@ispm/layout/classes/confirm-modal-loader-payload';
import {modalResponse} from '@ispm/layout/constants/modal-responses.enum';
import {NOTIFICATION_MESSAGE} from "@ispm-client/modules/aggregator/services/consts";
import {BrowserCacheService} from "@ispm/core/services/browser-cache.service";
import {select, Store} from '@ngrx/store';
import {throwError} from "rxjs";
import {selectPatient, selectPractitioner} from "@ispm/user/store/user.selector";
import {IPatient} from "@ispm/user/interfaces/patient.interface";
import {get} from 'lodash';
import { NotificationMessageService } from '@ispm/core/components/customise-notification/notifications-message.service';
import { USER_ACTIONS } from '../store/user-actions.constant';
import { IspmHttpClient } from '@ispm/core/services/ispm-http-client';
import {of} from "rxjs/observable/of";

@Injectable()
export class UserRequester {
  constructor(
    private _http: HttpInterceptorService,
    private _browserCacheService: BrowserCacheService,
  ) {
  }

  public getPatient(id: string, withLoader: boolean = false): Observable<any> {
    const url = `${UserService.patient}/${id}` +
      (localStorage.getItem('disableCache') !== 'true' ? `?cacheId=${this._browserCacheService.getCacheId()}&m` : '?m');

    return this._http.get(url, null, withLoader)
      .pipe(
        map(data => data.json()),
      );
  }
}

@Injectable()
export class UserService {

  constructor(
    private _http: HttpInterceptorService,
    private userAC: UserActionCreatorService,
    private _modalLoader: ModalLoaderNgjsService,
    private _browserCacheService: BrowserCacheService,
    private store: Store<any>,
    private userReq: UserRequester,
    private notificationMessageService: NotificationMessageService,
    private notificationMessage: NOTIFICATION_MESSAGE,
    private _ispmHttpClient: IspmHttpClient
  ) {
  }

  static patient = '/patient';
  static patientV2 = '/api/v2/patient';
  static practitioner = '/practitioner';

  public static GetUniqueFields(mrn?: string, identifier?: string): Array<ISpecificMapItem> {
    const uniqueArr = [];
    if (mrn) {
      uniqueArr.push({ 'identifier': { 'unique': 'true'}, 'key': 'MRN', 'value': mrn});
    }
    if (identifier) {
      uniqueArr.push({ 'identifier': { 'unique': 'true'}, 'key': 'UUID', 'value': identifier});
    }
    return uniqueArr;
  }

  private requestPractitioner(id: string): Observable<any> {
    return this._http.get(`${UserActionCreatorService.practitioner}/${id}`)
      .pipe(
        map(data => data.json()),
      );
  }

  public getPractitioner(id: string): Observable<Ipractitioner> {
    return this.userAC.restoreState(
      () => select(selectPractitioner, { id}),
      this.requestPractitioner(id)
        .pipe(
          tap(data => this.userAC.updatePractitioner(id, data)),
          catchError(err => {
            console.error('Failed to fetch Practitioner : Error : ', err.message);
            this.userAC.updatePractitioner(id, { });
            return throwError(err);
          }),
        )
    );
  }

  public getBySearchParam(searchParams: any, userType: usersTypes, countOnly: boolean = false, cache = true): Observable<Ipractitioner[] | any[]> {
    let searchUrl: string;

    switch (userType) {
      case usersTypes.PATIENT:
        searchUrl = UserService.patient;
        if(cache){
        searchParams['cacheId'] = this._browserCacheService.getCacheId();
        }
        break;
      case usersTypes.PRACTITIONER:
        searchUrl = UserService.practitioner;
        break;
      default:
        return Observable.throw('Invalid Type, use usersTypes');
    }

    searchUrl += '?';

    for (const key in searchParams) {
      searchUrl += `${key}=${searchParams[key]}&`;
    }

    searchUrl = countOnly ? searchUrl + '_countOnly=true' : searchUrl;

    return this._http.get(searchUrl)
      .map(data => data.json())
      .map(data => {
        if (!countOnly) {
          data.forEach(user => {
            switch (userType) {
              case usersTypes.PATIENT:
                this.userAC.updatePatient(user.id, user);
                break;
              case usersTypes.PRACTITIONER:
                this.userAC.updatePractitioner(user.id, user);
                break;
              default:
                return Observable.throw('Invalid Type, use usersTypes');
            }
          });
        }
        return data;
      });
  }

  public getPatient(id: string, setCurrentPatient: boolean = false, withLoader: boolean = false): Observable<IPatient> {
    return this.userAC.restoreState<IPatient>(
      () => select(selectPatient, { id }),
      this.userReq.getPatient(id, withLoader)
        .pipe(
          tap(data => this.userAC.updatePatient(id, data)),
          catchError(err => {
            console.error('Failed to fetch Patient : Error : ', err.message);
            this.userAC.updatePatient(id, { });
            return throwError(err);
          }),
        )
      )
      .pipe(
        tap(patient => (setCurrentPatient) && this.userAC.setCurrentPatient(patient)),
      );
  }

  updatePatient(patient: Patient) {
    return this._http.put(`${UserService.patientV2}/${patient.id}`, new GenericApiRequest<Patient>(patient, ...UserService.GetUniqueFields(null, patient.id)).getUpdateObject(), true, {'source': 'ISPM-WEB-CLIENT'})
      .pipe(
        catchError(err => {
          console.error('Failed to update Patient : Error : ', err.message);
          this.notificationMessageService.error(this.notificationMessage.UNABLE_TO_UPDATE_PATIENT);
          return Observable.throw(err);
        }),
        map(res => res.json()),
        tap(_ => this.notificationMessageService.success(this.notificationMessage.PATIENT_UPDATED_SUCCESSFULLY)),
        tap(patient => this.userAC.updatePatient(patient.id, patient))
      );
  }

  createPatient(patient: Patient) {
    return this._http.post(`${UserService.patientV2}`, new GenericApiRequest<Patient>(patient, ...UserService.GetUniqueFields(patient.MRN)), true, {'source': 'ISPM-WEB-CLIENT'})
      .pipe(
        map(res => {
          patient = res.json();
          this.notificationMessageService.success(this.notificationMessage.PATIENT_CREATED_SUCCESSFULLY);
          this.userAC.updatePatient(patient.id, patient);
          return patient;
        }),
        catchError(err => {
          console.error('Failed to create Patient : Error : ', err.message);
          if (err.status === 422) {
            /** MRN Already exists. Show confirm popup*/
            console.error('Patient Exist : Error : ', err.message);
            return this._modalLoader.popupModal('confirm',
              ConfirmModalLoaderPayload.builder().
              setTitle('Warning').
              setContent('Patient with same MRN already exists!  Do you want to continue with existing patient?')
                .setApproveButtonText('Continue')
                .setDeclineButtonText('Cancel'),
              'ALERT',
              'patientDuplicatePopupChannel'
            )
              .pipe(
                mergeMap((data: any) => {
                  if (data.payload === modalResponse.approve) {
                    this.notificationMessageService.success(this.notificationMessage.CONTINUING_WITH_EXISTING_PATIENT);
                    return this.userAC.getPatientByMRN(patient.getMRN().value)
                      .pipe(map( res => {
                        patient = res;
                        this.userAC.updatePatient(patient.id, patient);
                        return patient;
                      }));
                  }
                  else {
                    return Observable.of(false);
                  }
                })
              );
          }
          else {
            this.notificationMessageService.error(this.notificationMessage.UNABLE_TO_CREATE_PATIENT);
            return Observable.throw(err);
          }
        }));
  }

  /**
   * returns the form of the practitioner as drop down
   *
   * @param {string[]} roles - the roles that allowed to be fetched
   * @param {*} additionalParameters - dictionary of additional search parameters
   * @param {string} placeholder - string for placeholder
   * @param excludeValue
   * @param allExceptRoles
   * @param addOrganization
   *
   * @return {DynamicFormItem[]}
   */
  public getPractitionerInputForm(
    roles: string[],
    additionalParameters: any = { '_sort:desc': 'name.given'},
    placeholder?: string,
    excludeValue?: string,
    allExceptRoles?: boolean,
    addOrganization?: boolean
  ) {
    let queryString;


    if (allExceptRoles) {
      queryString = this.getAllExceptPractitionerQueryString(roles);
    } else {
      queryString = this.getPractitionerQueryString(roles);
    }

    if (additionalParameters) {
      additionalParameters['_sort:desc'] = 'name.given';
      if (addOrganization && this.userAC.authService.user && this.userAC.authService.user.organization){
          additionalParameters['organization'] = this.userAC.authService.user.organization;
      }


    }

    const transformPipe = '|transform=name[0].text:display,id:code';
    Object.keys(additionalParameters).forEach(key => {
      queryString += `&${key}=${additionalParameters[key]}`;
    });

    return [
      DynamicFormItem.builder('', 'practitioner', ITEMS_TYPES.DROPDOWN)
        .setOptions(queryString + transformPipe, excludeValue)
        .setPlaceholder(placeholder)
        .addFieldsToDisplay(
          {
            display: 'name[0]', pattern: '$(prefix).', conditions: [
              {
                operator: ConditionalOperator.HAS_VALUE,
                value: '',
                path: 'prefix[0]'
              }
            ]
          },
          { display: 'name[0].text'}
        )
        .setFieldTemplate('hide-main-value')
        .setCheckClicksOn('.p-autocomplete-items,.p-autocomplete-item,.main_value,.suggestion-wrapper')
        .setCssClass(' full-size-item no-margin-right')
        .setAttributes({ '[hideInvalidTooltip]': true, '[forceFormMode]': true,'[appendTo]':'body'})
    ];
  }

  private getPractitionerQueryString(roles: string[]) {
    let queryString = `/practitioner?name=$(searchTerm)&_fieldslist=id,name`;

    if (roles) {
      queryString += `&role=${roles}`;
    }

    return queryString;

  }

  private getAllExceptPractitionerQueryString(roles: string[]) {
    let queryString = `/practitioner?name=$(searchTerm)&_fieldslist=id,name`;

    if (roles) {
      queryString += `&role=!=${roles}`;
    }

    return queryString;
  }

  public getPatientForAberration(id: string, withLoader: boolean = false): any {
    let url = `${UserService.patient}/${id}`;
    if(localStorage.getItem('disableCache') != "true"){
      url = `${UserService.patient}/${id}?cacheId=${this._browserCacheService.getCacheId()}`
    }
    return this._http.get(url, null, withLoader);
  }

  public getPatientLink(id: string) {
    let patient_link_list: string[] = [];

    this.store.select('users','currentPatientLink')
      .take(1)
      .subscribe((data) => {
        patient_link_list = data;
    });

    if(patient_link_list == undefined || patient_link_list.length < 1){
      this.store.select('users','currentPatient')
      .take(1)
      .subscribe(async (data) => {
        if(data != undefined){
          patient_link_list = get(data, 'otherKnownIds');
          this.store.dispatch({ type: USER_ACTIONS.SET_CURRENT_PATIENTLINK, payload:patient_link_list});
        }
        else {

          let patientEntitytree = `entity-tree/patient/${id}?_fieldslist=otherKnownIds`;
          this._ispmHttpClient.get(patientEntitytree).subscribe(data=>{
              this.store.dispatch({ type: USER_ACTIONS.SET_CURRENT_PATIENTLINK, payload:data['otherKnownIds']});
          });
        }
      });
    }
  }
}
